import React from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useProjects } from '../../hooks/useProjects.jsx'
import { userRoles } from '../../data/mockData'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * Budget Allocation — real budget data across projects (from useProjects).
 * Shows portfolio totals, per-category breakdown, and per-project utilization.
 */
function BudgetAllocation() {
  const { t } = useTranslation('money')
  const { t: tc } = useTranslation('common')
  const { user } = useAuth()
  const { projects, loading } = useProjects()

  const canView = user?.role === userRoles.ADMIN || user?.role === userRoles.FINANCE || user?.role === userRoles.Funder

  if (!canView) {
    return (
      <div className="p-6"><div className="card"><div className="p-12 text-center">
        <h3 className="mt-4 text-lg font-medium text-gray-900">{t('access_restricted')}</h3>
        <p className="mt-2 text-gray-600">{t('no_perm_allocations')}</p>
      </div></div></div>
    )
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-7 h-7 text-indigo-600 animate-spin" /></div>
  }

  const num = (v) => Number(v || 0)
  const totalAllocated = projects.reduce((s, p) => s + num(p.approved_budget ?? p.total_budget), 0)
  const totalSpent = projects.reduce((s, p) => s + num(p.spent_budget), 0)
  const totalAvailable = totalAllocated - totalSpent
  const utilPct = totalAllocated > 0 ? Math.round((totalSpent / totalAllocated) * 100) : 0

  // Real per-category breakdown from project.category
  const byCategory = {}
  for (const p of projects) {
    const cat = p.category || 'General'
    if (!byCategory[cat]) byCategory[cat] = { allocated: 0, spent: 0 }
    byCategory[cat].allocated += num(p.approved_budget ?? p.total_budget)
    byCategory[cat].spent += num(p.spent_budget)
  }
  const categoryColors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-yellow-500', 'bg-pink-500', 'bg-cyan-500']

  const getUtilizationColor = (u) => {
    if (u >= 90) return 'text-red-600 bg-red-100'
    if (u >= 75) return 'text-yellow-600 bg-yellow-100'
    if (u >= 50) return 'text-blue-600 bg-blue-100'
    return 'text-green-600 bg-green-100'
  }

  return (
    <div className="p-6 space-y-6">
      {/* header */}
      <div className="bg-linear-to-r from-indigo-600 to-blue-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('budget_allocation')}</h1>
            <p className="mt-1 opacity-90">{t('monitor_all')}</p>
          </div>
          <div className="text-right">
            <p className="text-sm opacity-90">{t('total_allocated')}</p>
            <p className="text-3xl font-bold">${totalAllocated.toLocaleString()}</p>
            <p className="text-sm opacity-90">{utilPct}% utilized</p>
          </div>
        </div>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6"><p className="text-sm font-medium text-gray-600">{tc('labels.budget')}</p><p className="text-2xl font-bold text-gray-900">${totalAllocated.toLocaleString()}</p><p className="text-xs text-green-600">Across {projects.length} projects</p></div>
        <div className="card p-6"><p className="text-sm font-medium text-gray-600">{t('committed_spent')}</p><p className="text-2xl font-bold text-gray-900">${totalSpent.toLocaleString()}</p><p className="text-xs text-yellow-600">{utilPct}% of budget</p></div>
        <div className="card p-6"><p className="text-sm font-medium text-gray-600">{t('available')}</p><p className="text-2xl font-bold text-gray-900">${totalAvailable.toLocaleString()}</p><p className="text-xs text-green-600">{totalAllocated > 0 ? Math.round((totalAvailable / totalAllocated) * 100) : 0}% remaining</p></div>
        <div className="card p-6"><p className="text-sm font-medium text-gray-600">{t('active_projects')}</p><p className="text-2xl font-bold text-gray-900">{projects.filter(p => p.status === 'In Progress').length}</p><p className="text-xs text-purple-600">{t('currently_funded')}</p></div>
      </div>

      {/* Category breakdown */}
      <div className="card">
        <div className="p-6 border-b border-gray-200"><h3 className="text-lg font-semibold text-gray-900">{t('budget_by_category')}</h3></div>
        <div className="p-6 space-y-6">
          {Object.entries(byCategory).length === 0 ? (
            <p className="text-sm text-gray-400">{t('no_project_budgets')}</p>
          ) : Object.entries(byCategory).map(([name, c], i) => {
            const util = c.allocated > 0 ? (c.spent / c.allocated) * 100 : 0
            return (
              <div key={name}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full ${categoryColors[i % categoryColors.length]} mr-3`}></div>
                    <span className="font-medium text-gray-900">{name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">${c.allocated.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">${c.spent.toLocaleString()} spent</p>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                  <div className={`h-3 rounded-full ${categoryColors[i % categoryColors.length]}`} style={{ width: `${Math.min(util, 100)}%` }}></div>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${getUtilizationColor(util)}`}>{Math.round(util)}% utilized</span>
                  <span className="text-xs text-gray-500">${(c.allocated - c.spent).toLocaleString()} remaining</span>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Project allocations table */}
      <div className="card">
        <div className="p-6 border-b border-gray-200"><h3 className="text-lg font-semibold text-gray-900">{t('project_allocations')}</h3></div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Project', 'Total Budget', 'Approved', 'Spent', 'Remaining', 'Utilization'].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {projects.map((p) => {
                const approved = num(p.approved_budget ?? p.total_budget)
                const spent = num(p.spent_budget)
                const util = approved > 0 ? (spent / approved) * 100 : 0
                return (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{p.name}</div>
                      <div className="text-sm text-gray-500">{p.status}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${num(p.total_budget).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${approved.toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${spent.toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${num(p.remaining_budget ?? (approved - spent)).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-3">
                          <div className={`h-2 rounded-full ${util >= 90 ? 'bg-red-500' : util >= 75 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${Math.min(util, 100)}%` }}></div>
                        </div>
                        <span className="text-sm text-gray-900">{Math.round(util)}%</span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default BudgetAllocation
